import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { fieldsTypes } from 'config/constants';
import RuleFields from 'components/SlateViewsSettings/RuleFields/RuleFields';
import './FieldSelector.css';

const FieldSelector = ({ currentSUUID, fields, onChange, disabled, filterType, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const { t } = useTranslation();

  const targetType = filterType || fieldsTypes.RECORDS;

  // Build a hierarchy: target-type fields are selectable; groups that contain
  // them are rendered as non-selectable headers so the user has context which
  // one to choose when titles collide. Returned tree mirrors the source order.
  const recordFields = useMemo(() => {
    const buildNode = (field) => {
      if (field.type === targetType) {
        return { ...field, parent: undefined, children: undefined, isShown: true };
      }
      if (field.type === fieldsTypes.GROUP && field.children) {
        const childNodes = [];
        field.children.forEach((child) => {
          const node = buildNode(child);
          if (node) childNodes.push(node);
        });
        if (childNodes.length === 0) return null;
        return {
          ...field,
          parent: undefined,
          children: childNodes,
          isShown: true,
          disable: true,
        };
      }
      return null;
    };

    const result = [];
    if (fields) {
      fields.forEach((field) => {
        const node = buildNode(field);
        if (node) result.push(node);
      });
    }
    return result;
  }, [fields, targetType]);

  // Flat list of selectable fields, used to resolve the current selection's title.
  const flatSelectableFields = useMemo(() => {
    const result = [];
    const walk = (list) => {
      list.forEach((field) => {
        if (!field.disable) result.push(field);
        if (field.children) walk(field.children);
      });
    };
    walk(recordFields);
    return result;
  }, [recordFields]);

  const currentField = flatSelectableFields.find(
    (field) => field.suuid === currentSUUID,
  );

  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current) {
      var rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  var handleClickOutside = useCallback(function(event) {
    var trigger = triggerRef.current;
    var dropdown = dropdownRef.current;
    var clickedInTrigger = trigger && trigger.contains(event.target);
    var clickedInDropdown = dropdown && dropdown.contains(event.target);

    if (!clickedInTrigger && !clickedInDropdown) {
      setIsOpen(false);
    }
  }, []);

  useEffect(function() {
    document.addEventListener('mousedown', handleClickOutside);
    return function() {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Close dropdown when the page scrolls or user scrolls outside the dropdown
  useEffect(function() {
    if (!isOpen) return;
    var handlePageScroll = function(e) {
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setIsOpen(false);
    };
    window.addEventListener('scroll', handlePageScroll, true);
    window.addEventListener('wheel', handlePageScroll, true);
    return function() {
      window.removeEventListener('scroll', handlePageScroll, true);
      window.removeEventListener('wheel', handlePageScroll, true);
    };
  }, [isOpen]);

  var handleClick = function(e) {
    if (e) {
      e.preventDefault();
    }
    if (disabled) {
      return;
    }
    if (!isOpen) {
      updateDropdownPosition();
    }
    setIsOpen(!isOpen);
  };

  var handleFieldChange = function(field) {
    setIsOpen(false);
    onChange(field.suuid, field.title);
  };

  var onRemoveRule = function(e) {
    if (e) {
      e.stopPropagation();
    }
    if (disabled) {
      return;
    }
    onChange('', '');
  };

  var containerClasses = ['field-selector'];
  if (isOpen) containerClasses.push('field-selector--open');
  if (disabled) containerClasses.push('field-selector--disabled');
  if (error) containerClasses.push('field-selector--error');

  return (
    <div className={containerClasses.join(' ')}>
      <button
        ref={triggerRef}
        type="button"
        className="field-selector-trigger"
        onClick={handleClick}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={'field-selector-value' + (!currentField ? ' field-selector-value--placeholder' : '')}>
          {currentField ? currentField.title : t('settings:selectField')}
        </span>
        <span className={'field-selector-chevron' + (isOpen ? ' field-selector-chevron--open' : '')}>
          <span className="material-symbols-outlined">expand_more</span>
        </span>
      </button>
      {currentField && (
        <button
          type="button"
          className="field-selector-remove"
          onClick={onRemoveRule}
          disabled={disabled}
          aria-label={t('btn:remove')}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      )}
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="field-selector-portal"
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: dropdownPosition.width,
            zIndex: 9999,
          }}
        >
          <RuleFields fields={recordFields} onFieldClick={handleFieldChange} />
        </div>,
        document.body
      )}
    </div>
  );
};

FieldSelector.propTypes = {
  currentSUUID: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  filterType: PropTypes.string,
};

export default FieldSelector;
